import React, { useEffect, useState } from "react";
import Loading from "../../shared/ui/Loading";
import { useAuth } from "../../features/account/context/AuthContext";
import { useRuntimeConfig } from "../providers/RuntimeConfigContext";

const SESSION_READY_KEY = "app_ready";
const GATE_TIMEOUT_MS = 8000;

/**
 * LoadingGate - Blocks initial render until config/auth are ready.
 * Only gates on first load per session. Subsequent route changes are not gated.
 */
const LoadingGate = ({ children }) => {
  const { loading: authLoading } = useAuth();
  const { isLoading: configLoading, error: configError } = useRuntimeConfig();
  const [timedOut, setTimedOut] = useState(false);
  const [sessionReady] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_READY_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (sessionReady || configLoading) {
      return undefined;
    }

    const id = window.setTimeout(() => setTimedOut(true), GATE_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [configLoading, sessionReady]);

  const ready = !configLoading && (sessionReady || !authLoading || timedOut);

  if (configError) {
    throw configError;
  }

  useEffect(() => {
    if (!ready) return;
    try {
      if (!sessionReady) {
        sessionStorage.setItem(SESSION_READY_KEY, "1");
      }
    } catch {
      // ignore
    }
    // Ensure fade-in class is present regardless of bypass
    document.body.classList.add("app-ready");
  }, [ready, sessionReady]);

  if (!ready) {
    return <Loading fullscreen message="Preparing the Zone..." />;
  }
  return children;
};

export default LoadingGate;
